/**
 * UN CHOIX QUE L'ON PEUT CHERCHER.
 *
 * Demandé par l'utilisateur le 08/09/2026 : « pouvoir rechercher un client ou
 * un site sur l'OT car la liste devient de plus en plus grande ». Il remplace
 * le déroulant du CLIENT sur la commande et celui du SITE sur une étape — les
 * deux listes qui grossissent à chaque fiche créée.
 *
 *
 * POURQUOI PAS UN `<select>` — il n'y avait pas le choix
 * ---------------------------------------------------------------------------
 * Un déroulant natif ne se cherche pas : au clavier, il saute à la première
 * entrée qui commence par la lettre tapée, et c'est tout. Sur trois cents
 * clients, ça ne mène nulle part. Il faut donc un champ de saisie et une liste
 * dessous, écrits à la main — comme le reste de l'interface, sans dépendance.
 *
 *
 * CE QUI EST ENREGISTRÉ EST TOUJOURS UN IDENTIFIANT, JAMAIS DU TEXTE
 * ---------------------------------------------------------------------------
 * Le champ affiche un libellé, mais `quandChoisir` ne rend qu'un identifiant
 * pris dans la liste. On ne peut donc pas enregistrer un client qui n'existe
 * pas en tapant son nom : c'est ce qui distingue ce composant d'un champ libre,
 * et c'est ce que la base exige — `ordres_transport.client_id` et
 * `etapes.site_id` sont des clés étrangères.
 *
 * TAPER SANS CHOISIR NE CHANGE RIEN. Si l'on quitte le champ en ayant tapé
 * autre chose que ce qui était retenu, la valeur d'avant revient. Effacer un
 * choix se fait par la croix, pas en vidant le texte : sans cela, un client
 * disparaîtrait d'un ordre parce qu'on a cliqué à côté après avoir tapé trois
 * lettres.
 *
 *
 * LE CALCUL EST DANS `cherchable.ts`, éprouvé sans navigateur. Ce fichier ne
 * fait que dessiner et écouter.
 */

import { useEffect, useId, useRef, useState } from 'react';
import {
  CHOIX_AFFICHES,
  filtrerChoix,
  indiceSuivant,
  libelleDe,
  type Choix,
} from './cherchable.js';

export function ChoixCherchable({
  id,
  choix,
  valeur,
  invite,
  fautif = false,
  desactive = false,
  quandChoisir,
}: {
  readonly id: string;
  readonly choix: readonly Choix[];
  /** L'identifiant retenu, ou une chaîne vide. */
  readonly valeur: string;
  /** Ce qui s'affiche quand rien n'est choisi : « Choisir un client… ». */
  readonly invite: string;
  readonly fautif?: boolean;
  readonly desactive?: boolean;
  readonly quandChoisir: (id: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [survole, setSurvole] = useState(-1);
  const cadre = useRef<HTMLDivElement | null>(null);
  const champ = useRef<HTMLInputElement | null>(null);
  const listeId = useId();

  const retenu = libelleDe(choix, valeur);
  const filtres = ouvert ? filtrerChoix(choix, recherche) : choix;
  const montres = filtres.slice(0, CHOIX_AFFICHES);
  const reste = filtres.length - montres.length;

  /**
   * REFERMER QUAND ON CLIQUE AILLEURS.
   *
   * `mousedown` et non `click` : le clic finit peut-être sur un élément qui a
   * disparu entre-temps, et l'écouteur ne serait jamais appelé.
   */
  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e: MouseEvent) => {
      if (cadre.current !== null && !cadre.current.contains(e.target as Node)) {
        setOuvert(false);
        setRecherche('');
      }
    };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert]);

  const choisir = (choisiId: string) => {
    quandChoisir(choisiId);
    setOuvert(false);
    setRecherche('');
    setSurvole(-1);
  };

  const ouvrir = () => {
    if (desactive) return;
    setOuvert(true);
    setRecherche('');
    setSurvole(-1);
  };

  const auClavier = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!ouvert) {
        ouvrir();
        return;
      }
      setSurvole((s) =>
        indiceSuivant(s, montres.length, e.key === 'ArrowDown' ? 1 : -1),
      );
      return;
    }
    if (e.key === 'Enter' && ouvert) {
      // On n'enregistre QUE si une ligne est survolée : autrement, Entrée
      // enverrait le formulaire avec ce qui était déjà là, ce qui ressemble à
      // une validation de la recherche alors que rien n'a été choisi.
      e.preventDefault();
      const vise = montres[survole];
      if (vise !== undefined) choisir(vise.id);
      return;
    }
    if (e.key === 'Escape' && ouvert) {
      e.preventDefault();
      setOuvert(false);
      setRecherche('');
      setSurvole(-1);
    }
  };

  const classes = ['champ-cherchable'];
  if (fautif) classes.push('fautif');

  return (
    <div className="cadre-cherchable" ref={cadre}>
      <div className={classes.join(' ')}>
        <input
          id={id}
          ref={champ}
          type="text"
          className="saisie-cherchable"
          role="combobox"
          aria-expanded={ouvert}
          aria-controls={listeId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={desactive}
          /* L'INVITE EST TOUJOURS L'INVITE, jamais le choix retenu.
             Une première version mettait le client choisi en invite quand le
             champ s'ouvrait : le champ vide se lisait alors exactement comme
             le champ rempli, et on ne voyait pas qu'on était en train de
             taper. Constaté en s'en servant. */
          placeholder={invite}
          value={ouvert ? recherche : retenu}
          onFocus={ouvrir}
          onClick={ouvrir}
          onChange={(e) => {
            setRecherche(e.target.value);
            setSurvole(-1);
            if (!ouvert) setOuvert(true);
          }}
          onKeyDown={auClavier}
        />
        {/* LA CROIX EST LE SEUL MOYEN D'EFFACER UN CHOIX, et elle n'apparaît
            que s'il y en a un. Voir l'en-tête : vider le texte ne suffit pas,
            exprès. */}
        {valeur !== '' && !desactive && (
          <button
            type="button"
            className="vider-cherchable"
            aria-label="Effacer le choix"
            onClick={() => choisir('')}
          >
            ✕
          </button>
        )}
      </div>

      {ouvert && (
        <ul className="liste-cherchable" id={listeId} role="listbox">
          {montres.length === 0 ? (
            <li className="rien-cherchable">Aucune fiche ne correspond.</li>
          ) : (
            montres.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.id === valeur}
                  className={
                    i === survole ? 'ligne-cherchable survolee' : 'ligne-cherchable'
                  }
                  // Le champ doit GARDER LE FOCUS : sans cela, il perd le
                  // focus avant que le clic n'arrive, la liste se referme, et
                  // le clic tombe dans le vide.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setSurvole(i)}
                  onClick={() => choisir(c.id)}
                >
                  {c.libelle}
                  {/* La seconde ligne, quand le choix en porte une. Elle ne
                      remplace jamais le libellé : elle le précise. */}
                  {c.detail !== undefined && (
                    <span className="detail-cherchable">{c.detail}</span>
                  )}
                </button>
              </li>
            ))
          )}
          {reste > 0 && (
            <li className="reste-cherchable">
              et {String(reste)} autre{reste > 1 ? 's' : ''} — précisez votre recherche.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
